import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import './Footer.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin, faTwitter, faGithub, faYoutube } from '@fortawesome/free-brands-svg-icons';
import {
  faArrowRight,
  faChevronDown,
  faDownload,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import Cookies from 'js-cookie';
import { clearAnalyticsConsent, needsConsentPrompt, readAnalyticsConsent, setAnalyticsConsent } from '../../../../utils/analyticsConsent';
import { Link, useLocation } from 'react-router-dom';

const ChatInterface = lazy(() => import('../Foot/ChatInterface'));
const useProductKnowledgeCopilot = String(process.env.REACT_APP_PRODUCT_KNOWLEDGE_COPILOT || '').toLowerCase() !== 'false';

const CHAT_COLLAPSED_ROUTES = ['/', '/documentation', '/user-guide', '/quick-usage', '/cheat-code', '/guides'];

const FOOTER_ACTION_HIDDEN_PREFIXES = [
  '/dashboard',
  '/lite-dashboard',
  '/login',
  '/signup',
  '/forgot-password',
  '/verify',
  '/subscription',
  '/purchase',
  '/payment',
  '/sandbox',
  /* Pages that already close on their own call to action. The footer's "See
     MediaSFU in motion" band sat directly under each of them — two closing
     pitches back to back, the second one plainer than the first. The home
     page matches exactly ('/' only), since every path starts with '/'. */
  '/',
  '/home',
  '/products',
  '/solutions',
  '/demos',
  '/showcases',
  '/webrtc-api',
  '/webrtc-sfu-architecture',
  '/webrtc-scaling',
  '/managed-mediasoup',
];

const FOOTER_COLUMNS = [
  {
    title: 'Platform',
    links: [
      { name: 'Live demos', url: '/demos' },
      { name: 'Meetings', url: '/meeting' },
      { name: 'Cloud phone', url: '/telephony' },
      { name: 'AI agents', url: '/agents' },
      { name: 'Phone Studio', url: '/aiagents' },
      { name: 'Widgets', url: '/widgets' },
      { name: 'Widget Studio', url: '/widget-studio' },
      { name: 'Vibe Studio', url: '/vibe-studio' },
      { name: 'Live translation', url: '/translation' },
      { name: 'Cloud recording', url: '/recording' },
      { name: 'Download apps', url: '/download' },
    ],
  },
  {
    /* The solution pages had no footer link at all, so the only way to reach
       them was the nav menu. Buyers arrive by situation, not by feature. */
    title: 'Solutions',
    links: [
      { name: 'Customer care', url: '/solutions/call-centers' },
      { name: 'Remote teams', url: '/solutions/remote-teams' },
      { name: 'Education', url: '/solutions/edtech' },
      { name: 'Events & webinars', url: '/solutions/events' },
      { name: 'Live commerce', url: '/solutions/live-commerce' },
      { name: 'Healthcare', url: '/solutions/healthcare' },
      { name: 'Showcases', url: '/showcases' },
      { name: 'All solutions', url: '/solutions' },
    ],
  },
  {
    title: 'Developers',
    links: [
      { name: 'Docs portal', url: '/docs/', target: '_blank', rel: 'noopener noreferrer' },
      { name: 'Quick start', url: '/docs/usage/quickstart/', target: '_blank', rel: 'noopener noreferrer' },
      { name: 'SDK index', url: '/docs/sdks/', target: '_blank', rel: 'noopener noreferrer' },
      { name: 'API reference', url: '/docs/api/', target: '_blank', rel: 'noopener noreferrer' },
      { name: 'Interactive sandbox', url: '/sandbox' },
      { name: 'WebRTC & SFU guide', url: '/webrtc-api' },
      { name: 'Managed mediasoup', url: '/managed-mediasoup' },
      { name: 'Open mediasoup clients', url: 'https://github.com/mediasfu?tab=repositories&q=mediasoup-client', target: '_blank', rel: 'noopener noreferrer' },
      { name: 'Disposable keys', url: '/disposable-keys' },
      { name: 'Storybook preview', url: '/storybook/', target: '_blank', rel: 'noopener noreferrer' },
    ],
  },
  {
    /* Guides, plus the reading that used to sit under Company. The telephony
       and translation "guides" linked to the same pages as Cloud phone and
       Live translation above, so they are gone rather than listed twice. */
    title: 'Resources',
    links: [
      { name: 'Guides hub', url: '/guides' },
      { name: 'Dashboard user guide', url: '/user-guide' },
      { name: 'Quick usage guide', url: '/quick-usage' },
      { name: 'Developer console guide', url: '/documentation' },
      { name: 'Widget Studio guide', url: '/widget-studio-guide' },
      { name: 'AI notes guide', url: '/ai-notes-guide' },
      { name: 'Use cases', url: '/use-cases' },
      { name: 'Blog', url: '/blog' },
      { name: 'Customer forum', url: '/forums' },
    ],
  },
  {
    title: 'Company',
    links: [
      { name: 'About MediaSFU', url: '/about' },
      { name: 'Pricing', url: '/pricing' },
      { name: 'Security', url: '/security' },
      { name: 'Careers', url: '/careers' },
      { name: 'Contact', url: '/contact' },
    ],
  },
];

/* Every comparison by name, rather than one "Compare platforms" link. Someone
   evaluating searches "MediaSFU vs Twilio"; a sitewide link to each page is
   both the shortest route for them and the clearest signal of what the page
   is. Video infrastructure first, then voice-agent platforms. */
const COMPARISONS = [
  { name: 'Twilio', url: '/vs/twilio' },
  { name: 'Agora', url: '/vs/agora' },
  { name: 'LiveKit', url: '/vs/livekit' },
  { name: 'Daily', url: '/vs/daily' },
  { name: 'Vonage', url: '/vs/vonage' },
  { name: 'Vapi', url: '/vs/vapi' },
  { name: 'Retell AI', url: '/vs/retell' },
  { name: 'Bland AI', url: '/vs/bland' },
  { name: 'Pipecat', url: '/vs/pipecat' },
];

/* The /alternatives pages answer a different search ("Twilio alternatives",
   someone still shopping) from the /vs pages ("MediaSFU vs Twilio", someone
   already comparing us), so each gets its own row. Every slug here is a
   prerendered page in package.json reactSnap.include. */
const ALTERNATIVES = [
  { name: 'Twilio', url: '/alternatives/twilio' },
  { name: 'LiveKit', url: '/alternatives/livekit' },
  { name: 'Vapi', url: '/alternatives/vapi' },
  { name: 'Retell AI', url: '/alternatives/retell' },
  { name: 'Bland AI', url: '/alternatives/bland' },
];

const SOCIAL_LINKS = [
  { icon: faLinkedin, url: 'https://www.linkedin.com/company/mediasfu', label: 'MediaSFU on LinkedIn' },
  { icon: faTwitter, url: 'https://twitter.com/media_sfu', label: 'MediaSFU on X' },
  { icon: faYoutube, url: 'https://www.youtube.com/playlist?list=PL1JaYsvPqLVz-ucQ8OrT4bWVkJO3kY1kD', label: 'MediaSFU on YouTube' },
  { icon: faGithub, url: 'https://github.com/mediasfu', label: 'MediaSFU on GitHub' },
];

const shouldStartChatCollapsed = (pathname = '') =>
  CHAT_COLLAPSED_ROUTES.some((route) => pathname === route || (route !== '/' && pathname.startsWith(`${route}/`)));

const FooterLink = ({ link }) => {
  const { name, url, target, rel } = link;
  const isExternal = /^https?:\/\//.test(url) || Boolean(target);

  if (isExternal) {
    return <a href={url} target={target} rel={rel}>{name}</a>;
  }

  return <Link to={url}>{name}</Link>;
};

const Footer = () => {
  const location = useLocation();
  const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  // The notice is the analytics consent prompt (src/utils/analyticsConsent.js).
  // It appears only where consent is required (judged from the time zone) and
  // no choice is saved; elsewhere analytics runs and Cookie settings opts out.
  const [hideCookieBanner, setHideCookieBanner] = useState(
    () => readAnalyticsConsent() !== null || !needsConsentPrompt()
  );
  const [expandedGroups, setExpandedGroups] = useState({});

  const getInitialChatState = () => {
    if (isBrowser && shouldStartChatCollapsed(window.location.pathname)) return false;
    const storedPreference = Cookies.get('showChat');
    if (storedPreference === 'false') return false;
    if (isBrowser) return window.innerWidth > 768;
    return false;
  };

  const [showChat, setShowChat] = useState(getInitialChatState);
  const [isPageReady, setIsPageReady] = useState(() => {
    if (!isBrowser) return false;
    return document.readyState === 'complete';
  });

  const chooseAnalytics = (value) => {
    setAnalyticsConsent(value);
    setHideCookieBanner(true);
  };

  const openCookieSettings = () => {
    clearAnalyticsConsent();
    setHideCookieBanner(false);
  };

  const cookieBannerRef = useRef(null);

  /* The cookie notice and the Ask MediaSFU launcher are both position:fixed in
     the bottom-right corner, and the launcher's z-index (2147481000) is four
     orders of magnitude above the notice's (12050) -- so the launcher covered
     the Dismiss button and the notice could not be closed at all.

     Rather than exile the launcher to another corner permanently to dodge a
     banner shown once, publish the notice's measured height and let the
     launcher sit above it while it is there. Height is measured rather than
     assumed because the notice wraps to two lines on narrow viewports. */
  useEffect(() => {
    if (!isBrowser) return undefined;
    const node = cookieBannerRef.current;

    if (hideCookieBanner || !node) {
      delete document.body.dataset.cookieNotice;
      document.body.style.removeProperty('--cookie-notice-height');
      return undefined;
    }

    const publish = () => {
      document.body.dataset.cookieNotice = 'visible';
      document.body.style.setProperty(
        '--cookie-notice-height',
        `${Math.round(node.getBoundingClientRect().height)}px`
      );
    };

    publish();

    const observer =
      typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    if (observer) observer.observe(node);
    else window.addEventListener('resize', publish);

    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', publish);
      delete document.body.dataset.cookieNotice;
      document.body.style.removeProperty('--cookie-notice-height');
    };
  }, [hideCookieBanner, isBrowser]);

  const toggleGroup = (title) => {
    setExpandedGroups((current) => ({
      ...current,
      [title]: !current[title],
    }));
  };

  useEffect(() => {
    Cookies.set('showChat', showChat);
  }, [showChat]);

  useEffect(() => {
    if (shouldStartChatCollapsed(location.pathname)) {
      setShowChat(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isBrowser || isPageReady) return undefined;
    const handleLoad = () => setIsPageReady(true);
    window.addEventListener('load', handleLoad);
    return () => window.removeEventListener('load', handleLoad);
  }, [isBrowser, isPageReady]);

  const currentYear = new Date().getFullYear();
  const showFooterAction = !FOOTER_ACTION_HIDDEN_PREFIXES.some(
    (route) => location.pathname === route || location.pathname.startsWith(route + '/')
  );

  return (
    <>
      {isPageReady && !useProductKnowledgeCopilot && (
        <Suspense fallback={null}>
          <ChatInterface showChat={showChat} setShowChat={setShowChat} />
        </Suspense>
      )}

      <footer className="footer-modern">
        {showFooterAction && (
          <div className="footer-action">
            <div className="footer-action-copy">
              <span>From evaluation to a working workflow</span>
              <h2>See MediaSFU in motion, then choose how deep to build.</h2>
              <p>Start with a live product, keep the no-code path, or move into APIs and SDKs when you need control.</p>
            </div>
            <div className="footer-action-links">
              <Link to="/demos" className="footer-action-primary">
                <FontAwesomeIcon icon={faPlay} />
                Try live demos
              </Link>
              <Link to="/login" className="footer-action-secondary">
                Start free
                <FontAwesomeIcon icon={faArrowRight} />
              </Link>
              <Link to="/download" className="footer-action-download">
                <FontAwesomeIcon icon={faDownload} />
                Get the apps
              </Link>
            </div>
          </div>
        )}

        <div className="footer-container">
          <div className="footer-brand-col">
            <Link to="/" className="footer-brand-link">
              <img src="/images/logo.png" alt="" className="footer-logo" />
              <span>
                <strong className="footer-brand-name">MediaSFU</strong>
                <small>Real-time communication platform</small>
              </span>
            </Link>
            <p className="footer-description">
              Ready-made communication products and open infrastructure for meetings, phone, AI, translation, recording, and embedded experiences.
            </p>
            <div className="footer-social-links">
              {SOCIAL_LINKS.map(({ icon, url, label }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  title={label}
                  className="footer-social-icon"
                >
                  <FontAwesomeIcon icon={icon} />
                </a>
              ))}
            </div>
          </div>

          <div className="footer-navigation" aria-label="Footer navigation">
            {FOOTER_COLUMNS.map(({ title, links }) => {
              const isExpanded = Boolean(expandedGroups[title]);
              return (
                <section className={`footer-link-col${isExpanded ? ' is-expanded' : ''}`} key={title}>
                  <button
                    type="button"
                    className="footer-col-toggle"
                    onClick={() => toggleGroup(title)}
                    aria-expanded={isExpanded}
                    aria-controls={`footer-links-${title.toLowerCase()}`}
                  >
                    <span>{title}</span>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <h3 className="footer-col-title">{title}</h3>
                  <ul className="footer-links-list" id={`footer-links-${title.toLowerCase()}`}>
                    {links.map((link) => (
                      <li key={link.name}>
                        <FooterLink link={link} />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </div>

        <nav className="footer-compare" aria-label="Compare MediaSFU">
          <div className="footer-compare-row">
            <span className="footer-compare-lead">MediaSFU vs</span>
            <ul>
              {COMPARISONS.map(({ name, url }) => (
                <li key={url}>
                  <Link to={url}>{name}</Link>
                </li>
              ))}
              <li>
                <Link to="/vs" className="footer-compare-all">
                  All comparisons
                  <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              </li>
            </ul>
          </div>
          <div className="footer-compare-row">
            <span className="footer-compare-lead">Alternatives to</span>
            <ul>
              {ALTERNATIVES.map(({ name, url }) => (
                <li key={url}>
                  <Link to={url}>{name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="footer-bottom">
          <div className="footer-bottom-inner">
            <p className="footer-copyright">&copy; {currentYear} MediaSFU. All rights reserved.</p>
            <span className="footer-bottom-note">Build the workflow, not the plumbing.</span>
            <div className="footer-legal-links">
              <Link to="/privacy-policy">Privacy</Link>
              <Link to="/terms-of-service">Terms</Link>
              <Link to="/contact">Contact</Link>
              <button type="button" className="footer-cookie-settings" onClick={openCookieSettings}>
                Cookie settings
              </button>
            </div>
          </div>
        </div>

        {!hideCookieBanner && (
          <div className="footer-cookie-banner" ref={cookieBannerRef} role="region" aria-label="Cookie notice">
            <p>
              We use essential cookies to run the site, and Google Analytics to understand how it is used.
              <Link to="/privacy-policy"> Privacy details</Link>
            </p>
            <div className="footer-cookie-actions">
              <button type="button" onClick={() => chooseAnalytics('denied')} className="footer-cookie-secondary">
                Essential only
              </button>
              <button type="button" onClick={() => chooseAnalytics('granted')} className="footer-cookie-dismiss">
                Accept
              </button>
            </div>
          </div>
        )}
      </footer>
    </>
  );
};

export default Footer;
